import { GIFEncoder, quantize, applyPalette } from 'gifenc'
import {
  PITCH_LAYOUTS,
  buildSlots,
  type Card_,
} from './pitchLayouts'

// Shareable dream-team art for Stories — mirrors FootballPitchShell + PitchPlayerToken
// (striped grass, blue rim, yellow boards, round tokens). 9:16 for IG/Telegram.

export interface DreamTeamImageLabels {
  brandBoard: string
  stickerHint: string
  cta: string
  unknownInitial: string
}

export interface DreamTeamImageInput {
  teamName: string
  formation: string
  lineup: Record<string, Card_>
  matchLabel: string
  matchDate: string
  link: string
  labels: DreamTeamImageLabels
}

export function dreamTeamImageLabels(
  t: (key: string) => string,
): DreamTeamImageLabels {
  return {
    brandBoard: t('dreamTeam.brandBoard'),
    stickerHint: t('dreamTeam.image.stickerHint'),
    cta: t('dreamTeam.image.cta'),
    unknownInitial: t('dreamTeam.image.unknownInitial'),
  }
}

const FONT = "'Vazirmatn','Segoe UI',Tahoma,Arial,sans-serif"

const STORY_W = 540
const STORY_H = 960
const OUTER_PAD = 22
const HEADER_H = 138
const FOOTER_H = 120
/** Circular token size — a bit under in-app so 5-row formations fit cleanly. */
const TOKEN = 56
const NAME_H = 16
const BOARD = 26
const FIELD_INSET = 34
const ROW_GAP = 92
const PITCH_PAD_TOP = 14
const PITCH_PAD_BOTTOM = 22
const RIM = 4
const PITCH_RADIUS = 16

const GIF_FRAMES = 16
const GIF_DELAY_MS = 80
const GIF_COLORS = 96
const LOOP_SEC = (GIF_FRAMES * GIF_DELAY_MS) / 1000
const VIDEO_LOOPS = 2
const VIDEO_FPS = 16

const AVATAR_COLORS = ['#4f7cff', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4']
const PLACEHOLDER_SRC = '/placeholder.png'

export type DreamTeamShareMedia = {
  blob: Blob
  filename: string
  kind: 'video' | 'gif'
}

function roundedPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + rr, y)
  ctx.arcTo(x + w, y, x + w, y + h, rr)
  ctx.arcTo(x + w, y + h, x, y + h, rr)
  ctx.arcTo(x, y + h, x, y, rr)
  ctx.arcTo(x, y, x + w, y, rr)
  ctx.closePath()
}

function hash(str: string): number {
  let h = 0
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0
  return Math.abs(h)
}

function isPlaceholderPhotoUrl(src?: string): boolean {
  if (!src) return true
  const u = src.toLowerCase()
  return (
    u.includes('placehold') ||
    u.includes('dummyimage') ||
    u.includes('via.placeholder') ||
    u.includes('/640x360')
  )
}

function loadImageSafe(src?: string): Promise<HTMLImageElement | null> {
  if (!src) return Promise.resolve(null)
  return new Promise((resolve) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    const timer = setTimeout(() => resolve(null), 4000)
    img.onload = () => {
      clearTimeout(timer)
      resolve(img)
    }
    img.onerror = () => {
      clearTimeout(timer)
      resolve(null)
    }
    img.src = src
  })
}

/** Match .pitch-card-float: ease-in-out bob 0 → -10px → 0, staggered like PitchPlayerToken. */
function floatOffset(slotIndex: number, t: number): number {
  const delay = (slotIndex % 8) * 0.16
  const duration = 2.0 + (slotIndex % 4) * 0.25
  const timeSec = t * LOOP_SEC
  let u = ((timeSec - delay) / duration) % 1
  if (u < 0) u += 1
  return -7 * (1 - Math.cos(2 * Math.PI * u)) / 2
}

function fitLabel(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  const raw = (text || '').trim()
  if (!raw) return ''
  if (ctx.measureText(raw).width <= maxWidth) return raw
  const ellipsis = '…'
  let lo = 0
  let hi = raw.length
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2)
    const candidate = `${raw.slice(0, mid)}${ellipsis}`
    if (ctx.measureText(candidate).width <= maxWidth) lo = mid
    else hi = mid - 1
  }
  return lo > 0 ? `${raw.slice(0, lo)}${ellipsis}` : ellipsis
}

function drawCircularToken(
  ctx: CanvasRenderingContext2D,
  card: Card_,
  cx: number,
  top: number,
  img: HTMLImageElement | null,
  nameMaxWidth: number,
  unknownInitial: string,
) {
  const r = TOKEN / 2
  const cy = top + r

  // Soft shadow like .pitch-token box-shadow
  ctx.beginPath()
  ctx.arc(cx, cy + 2, r, 0, Math.PI * 2)
  ctx.fillStyle = 'rgba(12, 2, 50, 0.28)'
  ctx.fill()

  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)'
  ctx.fill()

  ctx.save()
  ctx.beginPath()
  ctx.arc(cx, cy, r - 1, 0, Math.PI * 2)
  ctx.clip()
  if (img) {
    const scale = Math.max(TOKEN / img.naturalWidth, TOKEN / img.naturalHeight)
    const w = img.naturalWidth * scale
    const h = img.naturalHeight * scale
    // object-position: top
    ctx.drawImage(img, cx - w / 2, top, w, h)
  } else {
    ctx.fillStyle = AVATAR_COLORS[hash(card.name) % AVATAR_COLORS.length]
    ctx.fillRect(cx - r, top, TOKEN, TOKEN)
    ctx.fillStyle = 'rgba(255,255,255,0.95)'
    ctx.font = `bold 22px ${FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.direction = 'ltr'
    ctx.fillText((card.name || unknownInitial).trim().charAt(0), cx, cy + 1)
  }
  ctx.restore()

  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.strokeStyle = 'rgba(18, 8, 70, 0.45)'
  ctx.lineWidth = 2
  ctx.stroke()

  // Name under token — width-aware like .pitch-token-name ellipsis
  ctx.font = `600 10px ${FONT}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  ctx.direction = 'ltr'
  const name = fitLabel(ctx, card.name, nameMaxWidth)
  ctx.fillStyle = 'rgba(0,0,0,0.55)'
  ctx.fillText(name, cx + 0.5, top + TOKEN + 3.5)
  ctx.fillStyle = '#ffffff'
  ctx.fillText(name, cx, top + TOKEN + 3)
}

function drawScrollingBoard(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  w: number,
  h: number,
  t: number,
  axis: 'x' | 'y',
) {
  ctx.save()
  ctx.beginPath()
  ctx.rect(x, y, w, h)
  ctx.clip()
  ctx.fillStyle = 'rgba(255, 220, 40, 0.82)'
  ctx.fillRect(x, y, w, h)
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)'
  ctx.lineWidth = 1
  ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1)

  ctx.fillStyle = '#1a1200'
  ctx.font = `800 11px ${FONT}`
  ctx.textAlign = 'left'
  ctx.textBaseline = 'middle'
  ctx.direction = 'rtl'

  const unit = `${text}  ·  `
  const unitW = Math.max(ctx.measureText(unit).width, 1)
  const track = axis === 'x' ? w : h
  const period = axis === 'x' ? 14 : 16
  const progress = ((t * LOOP_SEC) / period) % 1
  const offset = -progress * unitW
  const copies = Math.ceil(track / unitW) + 3

  if (axis === 'x') {
    for (let i = 0; i < copies; i++) {
      ctx.fillText(unit, x + offset + i * unitW, y + h / 2)
    }
  } else {
    // Vertical boards: rotate once, then tile along the long edge (no overlapping fills).
    ctx.translate(x + w / 2, y + h / 2)
    ctx.rotate(Math.PI / 2)
    for (let i = 0; i < copies; i++) {
      ctx.fillText(unit, -track / 2 + offset + i * unitW, 0)
    }
  }
  ctx.restore()
}

function drawBuilderPitch(
  ctx: CanvasRenderingContext2D,
  input: DreamTeamImageInput,
  photos: Map<string, HTMLImageElement | null>,
  t: number,
  pitchX: number,
  pitchY: number,
  pitchW: number,
  pitchH: number,
) {
  const rows = PITCH_LAYOUTS[input.formation] || PITCH_LAYOUTS['4-4-2']
  const slots = buildSlots(input.formation)

  // Outer chrome — .football-pitch
  roundedPath(ctx, pitchX, pitchY, pitchW, pitchH, PITCH_RADIUS)
  ctx.save()
  ctx.shadowColor = 'rgba(20, 3, 81, 0.55)'
  ctx.shadowBlur = 22
  ctx.fillStyle = 'rgba(37, 162, 28, 0.55)'
  ctx.fill()
  ctx.restore()

  roundedPath(ctx, pitchX, pitchY, pitchW, pitchH, PITCH_RADIUS)
  ctx.save()
  ctx.clip()

  // Striped grass
  const stripe = 28
  for (let i = 0; i * stripe < pitchW + stripe; i++) {
    ctx.fillStyle = i % 2 === 0 ? 'rgba(37, 162, 28, 0.55)' : 'rgba(32, 140, 24, 0.62)'
    ctx.fillRect(pitchX + i * stripe, pitchY, stripe, pitchH)
  }

  const brand = input.labels.brandBoard
  // Yellow LED boards — fixed brand text (team name already sits in the header)
  drawScrollingBoard(ctx, brand, pitchX + BOARD, pitchY, pitchW - BOARD * 2, BOARD, t, 'x')
  drawScrollingBoard(
    ctx,
    brand,
    pitchX + BOARD,
    pitchY + pitchH - BOARD,
    pitchW - BOARD * 2,
    BOARD,
    t,
    'x',
  )
  drawScrollingBoard(ctx, brand, pitchX, pitchY + BOARD, BOARD, pitchH - BOARD * 2, t, 'y')
  drawScrollingBoard(
    ctx,
    brand,
    pitchX + pitchW - BOARD,
    pitchY + BOARD,
    BOARD,
    pitchH - BOARD * 2,
    t,
    'y',
  )

  // Field area inside boards (leave room under last row for names)
  const fieldX = pitchX + FIELD_INSET
  const fieldY = pitchY + FIELD_INSET
  const fieldW = pitchW - FIELD_INSET * 2
  const fieldH = pitchH - FIELD_INSET * 2

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.28)'
  ctx.lineWidth = 1.5
  ctx.setLineDash([8, 7])
  const midY = fieldY + fieldH / 2
  ctx.beginPath()
  ctx.moveTo(fieldX + fieldW * 0.08, midY)
  ctx.lineTo(fieldX + fieldW * 0.92, midY)
  ctx.stroke()
  ctx.setLineDash([])

  ctx.beginPath()
  ctx.arc(fieldX + fieldW / 2, midY, 32, 0, Math.PI * 2)
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.28)'
  ctx.stroke()

  // Penalty boxes
  const penW = fieldW * 0.42
  const penH = fieldH * 0.14
  const penX = fieldX + (fieldW - penW) / 2
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)'
  ctx.strokeRect(penX, fieldY, penW, penH)
  ctx.strokeRect(penX, fieldY + fieldH - penH, penW, penH)

  const colW = fieldW / 5
  const nameMaxWidth = Math.max(colW - 6, TOKEN + 8)
  const slotBody = TOKEN + NAME_H
  const usable = Math.max(fieldH - PITCH_PAD_TOP - PITCH_PAD_BOTTOM, slotBody)
  const rowStep =
    rows.length > 1 ? Math.min(ROW_GAP, (usable - slotBody) / (rows.length - 1)) : 0
  const stackH = rows.length > 1 ? rowStep * (rows.length - 1) + slotBody : slotBody
  const stackTop = fieldY + PITCH_PAD_TOP + Math.max(0, (usable - stackH) / 2)

  let slotIndex = 0
  for (let row = 0; row < rows.length; row++) {
    const rowDef = rows[row]
    const baseTop = stackTop + (rows.length === 1 ? (usable - slotBody) / 2 : row * rowStep)
    for (const col of rowDef.cols) {
      const cx = fieldX + colW * (col + 0.5)
      const slot = slots.find((s) => s.row === row && s.col === col)
      const card = slot ? input.lineup[slot.key] : undefined
      if (!card || !slot) {
        slotIndex += 1
        continue
      }
      const top = baseTop + floatOffset(slotIndex, t)
      drawCircularToken(
        ctx,
        card,
        cx,
        top,
        photos.get(slot.key) ?? null,
        nameMaxWidth,
        input.labels.unknownInitial,
      )
      slotIndex += 1
    }
  }

  ctx.restore()

  // Blue rim on top of clip
  roundedPath(ctx, pitchX, pitchY, pitchW, pitchH, PITCH_RADIUS)
  ctx.strokeStyle = 'rgb(20, 10, 90)'
  ctx.lineWidth = RIM
  ctx.stroke()
}

function drawFrame(
  ctx: CanvasRenderingContext2D,
  input: DreamTeamImageInput,
  photos: Map<string, HTMLImageElement | null>,
  t: number,
) {
  const rows = PITCH_LAYOUTS[input.formation] || PITCH_LAYOUTS['4-4-2']
  const W = STORY_W
  const H = STORY_H

  ctx.clearRect(0, 0, W, H)

  const bg = ctx.createLinearGradient(0, 0, 0, H)
  bg.addColorStop(0, '#0c1017')
  bg.addColorStop(0.55, '#0a1220')
  bg.addColorStop(1, '#070b12')
  ctx.fillStyle = bg
  ctx.fillRect(0, 0, W, H)

  const glow = ctx.createRadialGradient(W / 2, 100, 10, W / 2, 140, 260)
  glow.addColorStop(0, 'rgba(123, 70, 255, 0.16)')
  glow.addColorStop(1, 'rgba(123, 70, 255, 0)')
  ctx.fillStyle = glow
  ctx.fillRect(0, 0, W, 320)

  ctx.textAlign = 'center'
  ctx.textBaseline = 'alphabetic'
  ctx.direction = 'rtl'
  ctx.fillStyle = '#8b93a7'
  ctx.font = `600 16px ${FONT}`
  ctx.fillText(`⚽ ${input.labels.brandBoard}`, W / 2, 40)
  ctx.fillStyle = '#ffffff'
  ctx.font = `bold 30px ${FONT}`
  ctx.fillText(fitLabel(ctx, input.teamName, W - OUTER_PAD * 2), W / 2, 78)
  ctx.fillStyle = '#c4b5fd'
  ctx.font = `600 15px ${FONT}`
  ctx.fillText(fitLabel(ctx, input.matchLabel, W - OUTER_PAD * 2), W / 2, 106)
  ctx.fillStyle = '#8b93a7'
  ctx.font = `400 13px ${FONT}`
  ctx.fillText(fitLabel(ctx, input.matchDate, W - OUTER_PAD * 2), W / 2, 128)

  const pitchW = W - OUTER_PAD * 2
  const neededRows = Math.max(rows.length - 1, 1)
  const neededPitchH =
    FIELD_INSET * 2 + PITCH_PAD_TOP + PITCH_PAD_BOTTOM + neededRows * ROW_GAP + TOKEN + NAME_H
  const pitchH = Math.min(neededPitchH, H - HEADER_H - FOOTER_H - 8)
  const pitchX = OUTER_PAD
  const pitchY = HEADER_H + Math.max(0, (H - HEADER_H - FOOTER_H - pitchH) / 2)

  drawBuilderPitch(ctx, input, photos, t, pitchX, pitchY, pitchW, pitchH)

  const footerY = H - FOOTER_H
  const pulse = 0.55 + 0.45 * (0.5 + 0.5 * Math.sin(t * Math.PI * 2))
  ctx.fillStyle = 'rgba(255,255,255,0.1)'
  ctx.fillRect(OUTER_PAD, footerY + 6, W - OUTER_PAD * 2, 1)

  const pillW = W - OUTER_PAD * 2
  const pillH = 40
  const pillX = OUTER_PAD
  const pillY = footerY + 22
  roundedPath(ctx, pillX, pillY, pillW, pillH, 20)
  ctx.fillStyle = `rgba(123, 70, 255, ${0.18 + pulse * 0.14})`
  ctx.fill()
  roundedPath(ctx, pillX, pillY, pillW, pillH, 20)
  ctx.strokeStyle = `rgba(123, 70, 255, ${0.45 + pulse * 0.35})`
  ctx.lineWidth = 2
  ctx.stroke()

  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.direction = 'ltr'
  ctx.fillStyle = '#ddd6fe'
  ctx.font = `bold 15px ${FONT}`
  const linkText = input.link.replace(/^https?:\/\//, '')
  ctx.fillText(fitLabel(ctx, linkText, pillW - 28), W / 2, pillY + pillH / 2)

  ctx.direction = 'rtl'
  ctx.fillStyle = '#8b93a7'
  ctx.font = `400 12px ${FONT}`
  ctx.textBaseline = 'alphabetic'
  ctx.fillText(input.labels.stickerHint, W / 2, footerY + 88)
  ctx.fillStyle = '#6b7385'
  ctx.font = `400 11px ${FONT}`
  ctx.fillText(input.labels.cta, W / 2, footerY + 106)
}

async function loadPhotos(
  input: DreamTeamImageInput,
  safeMode: boolean,
): Promise<Map<string, HTMLImageElement | null>> {
  const slots = buildSlots(input.formation)
  const filled = slots.filter((s) => input.lineup[s.key])
  const photos = new Map<string, HTMLImageElement | null>()
  if (safeMode) {
    filled.forEach((s) => photos.set(s.key, null))
    return photos
  }
  const fallback = await loadImageSafe(PLACEHOLDER_SRC)
  await Promise.all(
    filled.map(async (s) => {
      const url = input.lineup[s.key].photo_url
      const primary = isPlaceholderPhotoUrl(url) ? null : await loadImageSafe(url)
      photos.set(s.key, primary || fallback)
    }),
  )
  return photos
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob)
        else reject(new Error('canvas empty'))
      },
      'image/png',
    )
  })
}

async function renderStatic(input: DreamTeamImageInput, safeMode: boolean): Promise<Blob> {
  const photos = await loadPhotos(input, safeMode)
  const canvas = document.createElement('canvas')
  canvas.width = STORY_W
  canvas.height = STORY_H
  const ctx = canvas.getContext('2d')!
  drawFrame(ctx, input, photos, 0)
  return canvasToPngBlob(canvas)
}

export async function renderDreamTeamImage(input: DreamTeamImageInput): Promise<Blob> {
  try {
    return await renderStatic(input, false)
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'name' in err && (err as { name: string }).name === 'SecurityError') {
      return renderStatic(input, true)
    }
    throw err
  }
}

async function renderGif(input: DreamTeamImageInput, safeMode: boolean): Promise<Blob> {
  const photos = await loadPhotos(input, safeMode)
  const canvas = document.createElement('canvas')
  canvas.width = STORY_W
  canvas.height = STORY_H
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!

  const gif = GIFEncoder()
  for (let i = 0; i < GIF_FRAMES; i++) {
    const t = i / GIF_FRAMES
    drawFrame(ctx, input, photos, t)
    const { data } = ctx.getImageData(0, 0, STORY_W, STORY_H)
    const palette = quantize(data, GIF_COLORS)
    const index = applyPalette(data, palette)
    gif.writeFrame(index, STORY_W, STORY_H, {
      palette,
      delay: GIF_DELAY_MS,
      repeat: 0,
    })
  }
  gif.finish()
  const bytes = gif.bytes()
  const copy = new Uint8Array(bytes.byteLength)
  copy.set(bytes)
  return new Blob([copy], { type: 'image/gif' })
}

export async function renderDreamTeamGif(input: DreamTeamImageInput): Promise<Blob> {
  try {
    return await renderGif(input, false)
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'name' in err && (err as { name: string }).name === 'SecurityError') {
      return renderGif(input, true)
    }
    throw err
  }
}

function pickRecorderMime(): string | null {
  if (typeof MediaRecorder === 'undefined') return null
  const candidates = [
    'video/mp4',
    'video/webm;codecs=vp9',
    'video/webm;codecs=vp8',
    'video/webm',
  ]
  return candidates.find((m) => MediaRecorder.isTypeSupported(m)) ?? null
}

async function renderVideo(input: DreamTeamImageInput, safeMode: boolean): Promise<Blob | null> {
  const mime = pickRecorderMime()
  if (!mime || typeof HTMLCanvasElement === 'undefined') return null

  const photos = await loadPhotos(input, safeMode)
  const canvas = document.createElement('canvas')
  canvas.width = STORY_W
  canvas.height = STORY_H
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) return null

  // Seed first frame before captureStream so the recorder has pixels immediately
  drawFrame(ctx, input, photos, 0)

  let stream: MediaStream
  try {
    stream = canvas.captureStream(VIDEO_FPS)
  } catch {
    return null
  }

  const chunks: Blob[] = []
  let recorder: MediaRecorder
  try {
    recorder = new MediaRecorder(stream, {
      mimeType: mime,
      videoBitsPerSecond: 2_500_000,
    })
  } catch {
    stream.getTracks().forEach((t) => t.stop())
    return null
  }

  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data)
  }

  const durationMs = LOOP_SEC * VIDEO_LOOPS * 1000

  const blob = await new Promise<Blob | null>((resolve) => {
    const failTimer = window.setTimeout(() => {
      try {
        if (recorder.state !== 'inactive') recorder.stop()
      } catch {
        // ignore
      }
      stream.getTracks().forEach((t) => t.stop())
      resolve(null)
    }, durationMs + 4000)

    recorder.onerror = () => {
      window.clearTimeout(failTimer)
      stream.getTracks().forEach((t) => t.stop())
      resolve(null)
    }

    recorder.onstop = () => {
      window.clearTimeout(failTimer)
      stream.getTracks().forEach((t) => t.stop())
      if (chunks.length === 0) {
        resolve(null)
        return
      }
      resolve(new Blob(chunks, { type: mime }))
    }

    try {
      recorder.start(200)
    } catch {
      window.clearTimeout(failTimer)
      stream.getTracks().forEach((t) => t.stop())
      resolve(null)
      return
    }

    const started = performance.now()
    const tick = (now: number) => {
      const elapsed = now - started
      const t = (elapsed / 1000 / LOOP_SEC) % 1
      drawFrame(ctx, input, photos, t)
      if (elapsed < durationMs) {
        requestAnimationFrame(tick)
        return
      }
      // One last frame at rest, then stop
      drawFrame(ctx, input, photos, 0)
      window.setTimeout(() => {
        try {
          if (recorder.state !== 'inactive') recorder.stop()
        } catch {
          stream.getTracks().forEach((tr) => tr.stop())
          resolve(null)
        }
      }, 80)
    }
    requestAnimationFrame(tick)
  })

  return blob
}

/**
 * Instagram Stories freezes camera-roll GIFs to the first frame.
 * Prefer a short looping MP4/WebM (plays as video); fall back to animated GIF.
 */
export async function renderDreamTeamShareMedia(
  input: DreamTeamImageInput,
  baseName: string,
): Promise<DreamTeamShareMedia> {
  const safeBase = (baseName || 'dream-team').replace(/[^\w\u0600-\u06FF-]+/g, '-').slice(0, 40) || 'dream-team'

  const tryVideo = async (safeMode: boolean) => {
    try {
      return await renderVideo(input, safeMode)
    } catch {
      return null
    }
  }

  let video: Blob | null = null
  try {
    video = await tryVideo(false)
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'name' in err && (err as { name: string }).name === 'SecurityError') {
      video = await tryVideo(true)
    }
  }

  if (video && video.size > 1000) {
    const ext = video.type.includes('mp4') ? 'mp4' : 'webm'
    return { blob: video, filename: `${safeBase}.${ext}`, kind: 'video' }
  }

  const gif = await renderDreamTeamGif(input)
  return { blob: gif, filename: `${safeBase}.gif`, kind: 'gif' }
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Keep URL alive briefly so Safari can start the download
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}
